import { config } from '../config';
import { store } from '../store';

export interface AudioDevice {
  deviceId: string;
  label: string;
  kind: string;
}

export interface TranscriptionResult {
  transcript: string;
  isFinal: boolean;
  confidence: number;
  speaker?: 'salesperson' | 'customer';
  timestamp: Date;
  audioSource?: 'microphone' | 'speaker';
}

export interface DualCaptureConfig {
  customerDeviceId?: string;  // VB Cable device
  salespersonDeviceId?: string; // Microphone device
  enableCustomerCapture?: boolean;
  enableSalespersonCapture?: boolean;
}

export class AudioCaptureService {
  private socket: WebSocket | null = null;
  private customerMediaStream: MediaStream | null = null;
  private salespersonMediaStream: MediaStream | null = null;
  private customerAudioContext: AudioContext | null = null;
  private salespersonAudioContext: AudioContext | null = null;
  private isRecording = false;
  private onTranscriptionCallback: ((result: TranscriptionResult) => void) | null = null;
  private onAudioLevelCallback: ((level: number) => void) | null = null;
  private onConnectionStatusCallback: ((status: { deepgram: boolean }) => void) | null = null;
  private customerDeepgramSocket: WebSocket | null = null;
  private salespersonDeepgramSocket: WebSocket | null = null;
  private customerReconnectTimeout: NodeJS.Timeout | null = null;
  private salespersonReconnectTimeout: NodeJS.Timeout | null = null;
  private customerReconnectAttempts = 0;
  private salespersonReconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private customerDeviceLabel: string = '';
  private salespersonDeviceLabel: string = '';
  private dualCaptureConfig: DualCaptureConfig = {};
  private deepgramConnected = false;

  // Audio capture service initialized

  async getAudioDevices(): Promise<AudioDevice[]> {
    try {
      //First, request microphone permission to get proper device labels
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop()); // Stop immediately
      } catch (permissionError) {
        console.warn('⚠️ Microphone permission denied:', permissionError);
        throw new Error('Microphone permission required to detect devices');
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      
      const audioInputDevices = devices
        .filter(device => device.kind === 'audioinput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 8)}`,
          kind: device.kind
        }));      
      // If no devices with labels, try to get devices without permission
      if (audioInputDevices.length === 0 || audioInputDevices.every(d => d.label.includes('Microphone'))) {
        console.log('🔄 No labeled devices found, trying without permission...');
        
        // Try to get devices without requesting permission
        const devicesWithoutPermission = await navigator.mediaDevices.enumerateDevices();
        const fallbackDevices = devicesWithoutPermission
          .filter(device => device.kind === 'audioinput')
          .map(device => ({
            deviceId: device.deviceId,
            label: device.label || `Audio Input ${device.deviceId.slice(0, 8)}`,
            kind: device.kind
          }));
        
        return fallbackDevices;
      }

      return audioInputDevices;
    } catch (error) {
      console.error('❌ Error getting audio devices:', error);
      
      // Return a default device if all else fails
      return [{
        deviceId: 'default',
        label: 'Default Microphone',
        kind: 'audioinput'
      }];
    }
  }

  // New method to capture system audio using Web Audio API
  async startSystemAudioCapture(
    onTranscription?: (result: TranscriptionResult) => void,
    onAudioLevel?: (level: number) => void
  ): Promise<void> {
    try {
      
      // Check if getDisplayMedia is supported
      if (!navigator.mediaDevices.getDisplayMedia) {
        throw new Error('getDisplayMedia is not supported in this browser');
      }
      
      // Request screen capture with audio - try different constraint combinations
      let displayStream: MediaStream;
      
      try {
        // First try: minimal constraints
        displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: false,
          audio: true
        });
        console.log('✅ System audio capture started with minimal constraints');
      } catch (error) {
        console.log('⚠️ Minimal constraints failed, trying with video...');
        
        // Second try: with video (some browsers require video for audio capture)
        displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: true
        });
        console.log('✅ System audio capture started with video enabled');
      }

      console.log('✅ System audio capture started');
      
      // Create audio context for processing
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(displayStream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      
      // Connect the audio nodes
      source.connect(processor);
      processor.connect(audioContext.destination);
      
      // Process audio data
      processor.onaudioprocess = (event) => {
        const inputData = event.inputBuffer.getChannelData(0);
        
        // Calculate audio level
        if (onAudioLevel) {
          const level = this.calculateAudioLevel(inputData);
          onAudioLevel(level);
        }
        
        // Convert to Int16 for Deepgram
        const int16Data = this.convertToInt16(inputData);
        
        // Send to Deepgram for transcription
        if (this.customerDeepgramSocket && this.customerDeepgramSocket.readyState === WebSocket.OPEN) {
          this.customerDeepgramSocket.send(int16Data.buffer);
        }
      };
      
      // Store references
      this.customerMediaStream = displayStream;
      this.customerAudioContext = audioContext;
      this.isRecording = true;
      
      // Start Deepgram transcription
      await this.startCustomerDeepgramTranscription();
      
      console.log('✅ System audio capture and transcription started');
      
    } catch (error) {
      console.error('❌ Failed to start system audio capture:', error);
      
      // Provide more helpful error message
      if (error instanceof Error) {
        if (error.name === 'NotSupportedError') {
          throw new Error('System audio capture is not supported in this browser. Try using Chrome, Edge, or Firefox with screen sharing permissions.');
        } else if (error.name === 'NotAllowedError') {
          throw new Error('Screen sharing permission denied. Please allow screen sharing when prompted.');
        } else {
          throw new Error(`Failed to start system audio capture: ${error.message}`);
        }
      } else {
        throw new Error(`Failed to start system audio capture: ${error}`);
      }
    }
  }

  // System audio capture specifically for customer channel in dual capture
  private async startSystemAudioCaptureForCustomer(): Promise<void> {
    try {
      
      // Check if getDisplayMedia is supported
      if (!navigator.mediaDevices.getDisplayMedia) {
        throw new Error('getDisplayMedia is not supported in this browser');
      }
      
      // Request screen capture with audio - try different constraint combinations
      let displayStream: MediaStream;
      
      try {
        // First try: with both video and audio (most reliable)
        displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false
          }
        });
        console.log('✅ System audio capture started with video and audio');
      } catch (error) {
        console.log('⚠️ Video + audio failed, trying audio only...');
        
        // Second try: audio only
        displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: false,
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false
          }
        });
        console.log('✅ System audio capture started with audio only');
      }

      console.log('✅ System audio capture for customer started');
      
      // Check if we have audio tracks
      const audioTracks = displayStream.getAudioTracks();
      const videoTracks = displayStream.getVideoTracks();
    
      
      if (audioTracks.length === 0) {
        // Try to get audio from a different source or provide helpful error
        throw new Error('No audio tracks found. Please ensure you select a tab or application that is actively playing audio (like YouTube, Spotify, etc.) and that you grant audio permission when prompted.');
      }
      
      // Create audio context for processing
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(displayStream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      
      // Connect the audio nodes
      source.connect(processor);
      processor.connect(audioContext.destination);
      
      // Process audio data
      processor.onaudioprocess = (event) => {
        const inputData = event.inputBuffer.getChannelData(0);
        
        // Calculate audio level for customer channel
        if (this.onAudioLevelCallback) {
          const level = this.calculateAudioLevel(inputData);
          this.onAudioLevelCallback(level);
        }
        
        // Convert to Int16 for Deepgram
        const int16Data = this.convertToInt16(inputData);
        
        // Send to Deepgram for transcription
        if (this.customerDeepgramSocket && this.customerDeepgramSocket.readyState === WebSocket.OPEN) {
          this.customerDeepgramSocket.send(int16Data.buffer);
        }
      };
      
      // Store references
      this.customerMediaStream = displayStream;
      this.customerAudioContext = audioContext;
      this.customerDeviceLabel = 'System Audio (Customer)';
      
      // Start Deepgram transcription for customer
      await this.startCustomerDeepgramTranscription();
      
      console.log('✅ System audio capture for customer started successfully');
      
    } catch (error) {
      console.error('❌ Failed to start system audio capture for customer:', error);
      
      // Provide more helpful error message
      if (error instanceof Error) {
        if (error.name === 'NotSupportedError') {
          throw new Error('System audio capture is not supported in this browser. Try using Chrome, Edge, or Firefox with screen sharing permissions.');
        } else if (error.name === 'NotAllowedError') {
          throw new Error('Screen sharing permission denied. Please allow screen sharing when prompted.');
        } else if (error.message.includes('No audio tracks found')) {
          throw new Error('No audio detected. Please select a tab or application that is playing audio (like YouTube, Spotify, etc.) and try again.');
        } else {
          throw new Error(`Failed to start system audio capture: ${error.message}`);
        }
      } else {
        throw new Error(`Failed to start system audio capture: ${error}`);
      }
    }
  }

  async startCapture(
    deviceId?: string,
    onTranscription?: (result: TranscriptionResult) => void,
    onAudioLevel?: (level: number) => void
  ): Promise<void> {
    try {
      
      // Store callbacks
      this.onTranscriptionCallback = onTranscription || null;
      this.onAudioLevelCallback = onAudioLevel || null;

      // Start single device capture (legacy mode)
      if (deviceId) {
        await this.startSingleDeviceCapture(deviceId);
      }

      this.isRecording = true;
      console.log('🎙️ Single device audio capture started successfully');
      
    } catch (error) {
      console.error('❌ Failed to start audio capture:', error);
      throw error;
    }
  }

  async startDualCapture(
    config: DualCaptureConfig,
    onTranscription?: (result: TranscriptionResult) => void,
    onAudioLevel?: (level: number) => void
  ): Promise<void> {
    try {
      
      // Store callbacks
      this.onTranscriptionCallback = onTranscription || null;
      this.onAudioLevelCallback = onAudioLevel || null;
      this.dualCaptureConfig = config;

      // Start customer capture
      if (config.enableCustomerCapture) {
        if (config.customerDeviceId === 'system-audio') {
          // Use system audio capture for customer
          await this.startSystemAudioCaptureForCustomer();
        } else if (config.customerDeviceId) {
          // Use regular device capture for customer
          await this.startCustomerCapture(config.customerDeviceId);
        }
      }

      // Start salesperson capture (Microphone)
      if (config.enableSalespersonCapture && config.salespersonDeviceId) {
        await this.startSalespersonCapture(config.salespersonDeviceId);
      }

      this.isRecording = true;
      console.log('🎙️ Dual audio capture started successfully');
      
    } catch (error) {
      console.error('❌ Failed to start dual audio capture:', error);
      throw error;
    }
  }

  private async startSingleDeviceCapture(deviceId: string): Promise<void> {
    try {

      // Request microphone access
      // Note: Use flexible sampleRate for Windows compatibility
      const constraints: MediaStreamConstraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: { ideal: 16000 }, // Deepgram works best with 16kHz
          channelCount: 1,
          ...(deviceId && deviceId !== 'default' && { deviceId: { exact: deviceId } })
        }
      };

      

      // Use customer stream for single device mode
      this.customerMediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('✅ Audio access granted');
      
      // Get the actual device label for speaker detection
      const devices = await navigator.mediaDevices.enumerateDevices();
      const currentDevice = devices.find(d => d.deviceId === deviceId);
      this.customerDeviceLabel = currentDevice?.label || 'Unknown Device';
    

      // Initialize audio context
      this.customerAudioContext = new AudioContext();
      const source = this.customerAudioContext.createMediaStreamSource(this.customerMediaStream);

      // Create script processor for real-time audio processing
      const processor = this.customerAudioContext.createScriptProcessor(4096, 1, 1);
      
      let audioFrameCount = 0;
      processor.onaudioprocess = (event: AudioProcessingEvent) => {
        if (this.isRecording) {
          const audioData = event.inputBuffer.getChannelData(0);

          // Calculate audio level
          const audioLevel = this.calculateAudioLevel(audioData);

          // Call audio level callback
          if (this.onAudioLevelCallback) {
            this.onAudioLevelCallback(audioLevel);
          }

          // Send to Deepgram if connected
          if (this.customerDeepgramSocket && this.customerDeepgramSocket.readyState === WebSocket.OPEN) {
            const audioBuffer = this.convertToInt16(audioData);
            this.customerDeepgramSocket.send(audioBuffer);

            // Log every 100th frame to verify audio is being sent
            audioFrameCount++;
            if (audioFrameCount % 100 === 0) {
              console.log('🎵 Sent', audioFrameCount, 'audio frames to Deepgram | Level:', audioLevel.toFixed(3));
            }
          } else {
            if (audioFrameCount % 100 === 0) {
              console.warn('⚠️ Deepgram socket not ready:', this.customerDeepgramSocket?.readyState);
            }
            audioFrameCount++;
          }
        }
      };

      source.connect(processor);
      processor.connect(this.customerAudioContext.destination);

      // Start Deepgram transcription
      await this.startCustomerDeepgramTranscription();
      
      
    } catch (error) {
      console.error('❌ Failed to start single device audio capture:', error);
      throw error;
    }
  }

  private async startCustomerCapture(deviceId: string): Promise<void> {
    try {

      // Request microphone access for customer device
      // Note: Use flexible sampleRate for Windows compatibility
      const constraints: MediaStreamConstraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: { ideal: 16000 }, // Deepgram works best with 16kHz
          channelCount: 1,
          ...(deviceId && deviceId !== 'default' && { deviceId: { exact: deviceId } })
        }
      };

      

      this.customerMediaStream = await navigator.mediaDevices.getUserMedia(constraints);

      
      // Get the actual device label for speaker detection
      const devices = await navigator.mediaDevices.enumerateDevices();
      const currentDevice = devices.find(d => d.deviceId === deviceId);
      this.customerDeviceLabel = currentDevice?.label || 'Unknown Customer Device';
      
      

      // Initialize audio context
      this.customerAudioContext = new AudioContext();
      const source = this.customerAudioContext.createMediaStreamSource(this.customerMediaStream);

      // Create script processor for real-time audio processing
      const processor = this.customerAudioContext.createScriptProcessor(4096, 1, 1);
      
      processor.onaudioprocess = (event: AudioProcessingEvent) => {
        if (this.isRecording) {
          const audioData = event.inputBuffer.getChannelData(0);
          
          // Send to Deepgram if connected
          if (this.customerDeepgramSocket && this.customerDeepgramSocket.readyState === WebSocket.OPEN) {
            const audioBuffer = this.convertToInt16(audioData);
            this.customerDeepgramSocket.send(audioBuffer);
          }
        }
      };

      source.connect(processor);
      processor.connect(this.customerAudioContext.destination);

      // Start Deepgram transcription for customer
      await this.startCustomerDeepgramTranscription();
      
      
    } catch (error) {
      console.error('❌ Failed to start customer audio capture:', error);
      throw error;
    }
  }

  private async startSalespersonCapture(deviceId: string): Promise<void> {
    try {

      // Request microphone access for salesperson device
      // Note: Use flexible sampleRate for Windows compatibility
      const constraints: MediaStreamConstraints = {
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: { ideal: 16000 }, // Deepgram works best with 16kHz
          channelCount: 1,
          ...(deviceId && deviceId !== 'default' && { deviceId: { exact: deviceId } })
        }
      };

      

      this.salespersonMediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      
      // Get the actual device label for speaker detection
      const devices = await navigator.mediaDevices.enumerateDevices();
      const currentDevice = devices.find(d => d.deviceId === deviceId);
      this.salespersonDeviceLabel = currentDevice?.label || 'Unknown Salesperson Device';
    

      // Initialize audio context
      this.salespersonAudioContext = new AudioContext();
      const source = this.salespersonAudioContext.createMediaStreamSource(this.salespersonMediaStream);

      // Create script processor for real-time audio processing
      const processor = this.salespersonAudioContext.createScriptProcessor(4096, 1, 1);
      
      processor.onaudioprocess = (event: AudioProcessingEvent) => {
        if (this.isRecording) {
          const audioData = event.inputBuffer.getChannelData(0);
          
          // Calculate audio level for salesperson
          const audioLevel = this.calculateAudioLevel(audioData);
          
          // Call audio level callback
          if (this.onAudioLevelCallback) {
            this.onAudioLevelCallback(audioLevel);
          }
          
          // Send to Deepgram if connected
          if (this.salespersonDeepgramSocket && this.salespersonDeepgramSocket.readyState === WebSocket.OPEN) {
            const audioBuffer = this.convertToInt16(audioData);
            this.salespersonDeepgramSocket.send(audioBuffer);
          }
        }
      };

      source.connect(processor);
      processor.connect(this.salespersonAudioContext.destination);

      // Start Deepgram transcription for salesperson
      await this.startSalespersonDeepgramTranscription();
      
      
    } catch (error) {
      console.error('❌ Failed to start salesperson audio capture:', error);
      throw error;
    }
  }

  private calculateAudioLevel(audioData: Float32Array): number {
    // Calculate RMS (Root Mean Square) for audio level
    const sum = audioData.reduce((acc, sample) => acc + sample * sample, 0);
    const rms = Math.sqrt(sum / audioData.length);
    
    // Normalize to 0-1 range and apply some smoothing
    const normalizedLevel = Math.min(1, rms * 10); // Scale factor for better visualization
    
    return normalizedLevel;
  }



  private async startCustomerDeepgramTranscription(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        

        // Connect to our Deepgram proxy service using config
        const baseProxyUrl = config.server.deepgramUrl;

        // Get room_id from sessionManager (this will be the bot_id if available)
        import('./sessionManager').then(({ sessionManager }) => {
          const roomId = sessionManager.getRoomId() || 'default';

          // Get the actual sample rate from AudioContext (48000 on Windows, 44100 on Mac)
          const sampleRate = this.customerAudioContext?.sampleRate || 48000;
          console.log('🎵 Using sample rate for Deepgram:', sampleRate, 'Hz');

          // Add room_id and audio parameters as query parameters
          const separator = baseProxyUrl.includes('?') ? '&' : '?';
          const proxyUrl = `${baseProxyUrl}${separator}room_id=${encodeURIComponent(roomId)}&encoding=linear16&sample_rate=${sampleRate}&channels=1`;

          // No authentication required - connect directly
          this.customerDeepgramSocket = new WebSocket(proxyUrl);

          this.customerDeepgramSocket.onopen = () => {
            console.log('✅ Customer Deepgram WebSocket opened successfully');
            this.deepgramConnected = true;
            this.notifyConnectionStatus();
            resolve(); // Resolve the promise when connection opens
          };

          this.customerDeepgramSocket.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data);
              this.handleCustomerDeepgramMessage(data);
            } catch (error) {
              console.error('❌ Error parsing customer Deepgram message:', error);
            }
          };

          this.customerDeepgramSocket.onerror = (error) => {
            console.error('❌ Customer Deepgram WebSocket error:', error);
            console.log('🔍 Error details:', {
              readyState: this.customerDeepgramSocket?.readyState,
              url: this.customerDeepgramSocket?.url?.split('authorization=')[0] + 'authorization=***'
            });
            reject(error);
          };

          this.customerDeepgramSocket.onclose = (event) => {
            console.log('🔌 Customer Deepgram WebSocket closed:', event.code, event.reason);
            // Attempt to reconnect if recording is still active
            if (this.isRecording && this.customerReconnectAttempts < this.maxReconnectAttempts) {
              this.reconnectCustomerDeepgram();
            }
          };
        }).catch(error => {
          console.error('❌ Failed to import sessionManager:', error);
          reject(error);
        });

      } catch (error) {
        console.error('❌ Failed to start customer Deepgram transcription:', error);
        reject(error);
      }
    });
  }

  private async startSalespersonDeepgramTranscription(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {

        // Connect to our Deepgram proxy service using config
        const baseProxyUrl = config.server.deepgramUrl;

        // Get room_id from sessionManager (this will be the bot_id if available)
        import('./sessionManager').then(({ sessionManager }) => {
          const roomId = sessionManager.getRoomId() || 'default';

          // Get the actual sample rate from AudioContext (48000 on Windows, 44100 on Mac)
          const sampleRate = this.salespersonAudioContext?.sampleRate || 48000;

          // Add room_id and audio parameters as query parameters
          const separator = baseProxyUrl.includes('?') ? '&' : '?';
          const proxyUrl = `${baseProxyUrl}${separator}room_id=${encodeURIComponent(roomId)}&encoding=linear16&sample_rate=${sampleRate}&channels=1`;

          // No authentication required - connect directly
          this.salespersonDeepgramSocket = new WebSocket(proxyUrl);

          this.salespersonDeepgramSocket.onopen = () => {
            console.log('✅ Salesperson Deepgram WebSocket opened successfully');
            this.deepgramConnected = true;
            this.notifyConnectionStatus();
            resolve(); // Resolve the promise when connection opens
          };

          this.salespersonDeepgramSocket.onmessage = (event) => {
            try {
              const data = JSON.parse(event.data);
              this.handleSalespersonDeepgramMessage(data);
            } catch (error) {
              console.error('❌ Error parsing salesperson Deepgram message:', error);
            }
          };

          this.salespersonDeepgramSocket.onerror = (error) => {
            console.error('❌ Salesperson Deepgram WebSocket error:', error);
            console.log('🔍 Error details:', {
              readyState: this.salespersonDeepgramSocket?.readyState,
              url: this.salespersonDeepgramSocket?.url?.split('authorization=')[0] + 'authorization=***'
            });
            reject(error);
          };

          this.salespersonDeepgramSocket.onclose = (event) => {
            console.log('🔌 Salesperson Deepgram WebSocket closed:', event.code, event.reason);
            // Attempt to reconnect if recording is still active
            if (this.isRecording && this.salespersonReconnectAttempts < this.maxReconnectAttempts) {
              this.reconnectSalespersonDeepgram();
            }
          };
        }).catch(error => {
          console.error('❌ Failed to import sessionManager:', error);
          reject(error);
        });

      } catch (error) {
        console.error('❌ Failed to start salesperson Deepgram transcription:', error);
        reject(error);
      }
    });
  }

  private handleCustomerDeepgramMessage(data: any): void {
    // Handle Results messages with transcripts
    if (data.type === 'Results' && data.channel?.alternatives?.[0]?.transcript) {
      const transcript = data.channel.alternatives[0].transcript;

      // Only process non-empty transcripts
      if (transcript && transcript.trim()) {
        // For single device mode, assume it's the salesperson speaking
        const speaker: 'salesperson' = 'salesperson';
        const audioSource: 'microphone' = 'microphone';

        const result: TranscriptionResult = {
          transcript: transcript,
          isFinal: data.is_final || false,
          confidence: data.channel.alternatives[0].confidence || 0,
          speaker: speaker,
          audioSource: audioSource,
          timestamp: new Date()
        };

        if (this.onTranscriptionCallback) {
          this.onTranscriptionCallback(result);
        }
      }
    }


    // Log any errors
    if (data.type === 'Error' || data.error) {
      console.error('❌ Deepgram error:', data);
    }
  }

  private reconnectCustomerDeepgram(): void {
    this.customerReconnectAttempts++;

    // Clear existing timeout if any
    if (this.customerReconnectTimeout) {
      clearTimeout(this.customerReconnectTimeout);
    }

    // Wait 2 seconds before reconnecting
    this.customerReconnectTimeout = setTimeout(async () => {
      try {
        // Close existing socket if still open
        if (this.customerDeepgramSocket) {
          this.customerDeepgramSocket.close();
          this.customerDeepgramSocket = null;
        }

        // Reconnect
        await this.startCustomerDeepgramTranscription();
        this.customerReconnectAttempts = 0; // Reset on successful reconnection
        console.log('✅ Customer Deepgram reconnected successfully');
      } catch (error) {
        console.error('❌ Failed to reconnect customer Deepgram:', error);
        // Try again if we haven't exceeded max attempts
        if (this.customerReconnectAttempts < this.maxReconnectAttempts && this.isRecording) {
          this.reconnectCustomerDeepgram();
        }
      }
    }, 2000);
  }

  private reconnectSalespersonDeepgram(): void {
    this.salespersonReconnectAttempts++;
    console.log(`🔄 Attempting to reconnect salesperson Deepgram (attempt ${this.salespersonReconnectAttempts}/${this.maxReconnectAttempts})...`);

    // Clear existing timeout if any
    if (this.salespersonReconnectTimeout) {
      clearTimeout(this.salespersonReconnectTimeout);
    }

    // Wait 2 seconds before reconnecting
    this.salespersonReconnectTimeout = setTimeout(async () => {
      try {
        // Close existing socket if still open
        if (this.salespersonDeepgramSocket) {
          this.salespersonDeepgramSocket.close();
          this.salespersonDeepgramSocket = null;
        }

        // Reconnect
        await this.startSalespersonDeepgramTranscription();
        this.salespersonReconnectAttempts = 0; // Reset on successful reconnection
        console.log('✅ Salesperson Deepgram reconnected successfully');
      } catch (error) {
        console.error('❌ Failed to reconnect salesperson Deepgram:', error);
        // Try again if we haven't exceeded max attempts
        if (this.salespersonReconnectAttempts < this.maxReconnectAttempts && this.isRecording) {
          this.reconnectSalespersonDeepgram();
        }
      }
    }, 2000);
  }

  private handleSalespersonDeepgramMessage(data: any): void {

    // Handle Results messages with transcripts
    if (data.type === 'Results' && data.channel?.alternatives?.[0]?.transcript) {
      const transcript = data.channel.alternatives[0].transcript;
      
      // Only process non-empty transcripts
      if (transcript && transcript.trim()) {
        // Salesperson transcripts come from microphone
        const speaker: 'salesperson' = 'salesperson';
        const audioSource: 'microphone' = 'microphone';
        
        const result: TranscriptionResult = {
          transcript: transcript,
          isFinal: data.is_final || false,
          confidence: data.channel.alternatives[0].confidence || 0,
          speaker: speaker,
          audioSource: audioSource,
          timestamp: new Date()
        };
        
        if (this.onTranscriptionCallback) {
          this.onTranscriptionCallback(result);
        }
      }
    }
    
    // Handle SpeechStarted messages (optional logging)
    if (data.type === 'SpeechStarted') {
      // console.log('🎤 Salesperson speech started detected');
    }
  }

  private convertToInt16(float32Array: Float32Array): Int16Array {
    const int16Array = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      const s = Math.max(-1, Math.min(1, float32Array[i]));
      int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return int16Array;
  }

  stopCapture(): void {
    console.log('🛑 Stopping audio capture...');

    this.isRecording = false;
    this.deepgramConnected = false;
    this.notifyConnectionStatus();

    // Clear any pending reconnection timeouts
    if (this.customerReconnectTimeout) {
      clearTimeout(this.customerReconnectTimeout);
      this.customerReconnectTimeout = null;
    }
    if (this.salespersonReconnectTimeout) {
      clearTimeout(this.salespersonReconnectTimeout);
      this.salespersonReconnectTimeout = null;
    }

    // Reset reconnection attempts
    this.customerReconnectAttempts = 0;
    this.salespersonReconnectAttempts = 0;
    this.customerDeviceLabel = '';
    this.salespersonDeviceLabel = '';

    // Close customer Deepgram WebSocket
    if (this.customerDeepgramSocket) {
      this.customerDeepgramSocket.close();
      this.customerDeepgramSocket = null;
      console.log('🔌 Customer Deepgram WebSocket closed');
    }

    // Close salesperson Deepgram WebSocket
    if (this.salespersonDeepgramSocket) {
      this.salespersonDeepgramSocket.close();
      this.salespersonDeepgramSocket = null;
      console.log('🔌 Salesperson Deepgram WebSocket closed');
    }

    // Close WebSocket if exists
    if (this.socket) {
      this.socket.close();
      this.socket = null;
      console.log('🔌 Main WebSocket closed');
    }

    // Stop customer media stream
    if (this.customerMediaStream) {
      this.customerMediaStream.getTracks().forEach((track: MediaStreamTrack) => {
        track.stop();
        console.log('🛑 Stopped customer audio track:', track.label);
      });
      this.customerMediaStream = null;
    }

    // Stop salesperson media stream
    if (this.salespersonMediaStream) {
      this.salespersonMediaStream.getTracks().forEach((track: MediaStreamTrack) => {
        track.stop();
        console.log('🛑 Stopped salesperson audio track:', track.label);
      });
      this.salespersonMediaStream = null;
    }

    // Close customer audio context
    if (this.customerAudioContext) {
      this.customerAudioContext.close();
      this.customerAudioContext = null;
      console.log('🔌 Customer audio context closed');
    }

    // Close salesperson audio context
    if (this.salespersonAudioContext) {
      this.salespersonAudioContext.close();
      this.salespersonAudioContext = null;
      console.log('🔌 Salesperson audio context closed');
    }

    console.log('✅ Audio capture stopped and all connections closed');
  }

  isCapturing(): boolean {
    return this.isRecording;
  }

  // Enhanced cleanup method for complete disconnection
  disconnect(): void {
    console.log('🔌 Disconnecting audio capture service...');
    this.stopCapture();

    // Clear callbacks
    this.onTranscriptionCallback = null;
    this.onAudioLevelCallback = null;

    console.log('✅ Audio capture service disconnected');
  }

  // Method to reconnect Deepgram sockets with new room_id (when bot_id changes)
  async reconnectWithNewRoomId(): Promise<void> {
    if (!this.isRecording) {
      console.log('🔄 Not recording, skipping Deepgram reconnection');
      return;
    }

    console.log('🔄 Reconnecting Deepgram sockets with new room_id...');

    // Close existing Deepgram connections
    if (this.customerDeepgramSocket) {
      this.customerDeepgramSocket.close();
      this.customerDeepgramSocket = null;
    }
    if (this.salespersonDeepgramSocket) {
      this.salespersonDeepgramSocket.close();
      this.salespersonDeepgramSocket = null;
    }

    // Wait a moment for cleanup
    await new Promise(resolve => setTimeout(resolve, 100));

    // Reconnect with new room_id from sessionManager
    try {
      if (this.customerMediaStream) {
        await this.startCustomerDeepgramTranscription();
      }
      if (this.salespersonMediaStream) {
        await this.startSalespersonDeepgramTranscription();
      }
      console.log('✅ Deepgram sockets reconnected with new room_id');
    } catch (error) {
      console.error('❌ Failed to reconnect Deepgram sockets:', error);
    }
  }

  // Connection status tracking methods
  public onConnectionStatus(callback: (status: { deepgram: boolean }) => void): void {
    this.onConnectionStatusCallback = callback;
  }

  public getConnectionStatus(): { deepgram: boolean } {
    return { deepgram: this.deepgramConnected };
  }

  private notifyConnectionStatus(): void {
    if (this.onConnectionStatusCallback) {
      this.onConnectionStatusCallback({ deepgram: this.deepgramConnected });
    }
  }

}

export const audioCaptureService = new AudioCaptureService(); 